import React, { useEffect } from "react";
import SystemInput from "../../components/SystemInput";
import { V2_ROUTES } from "../state/useV2Navigation";
import { useV2Theme } from "../theme/ThemeProvider";
import { v2cx } from "../theme/tokens";
import { emitGuideEvent } from "../guide/guideEvents";
import { useOptionalProductGuide } from "../guide/GuideContext";
import { useMobileChrome } from "../mobile/MobileChromeContext";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";

/**
 * Add knowledge as its own nav destination (Phase 1 of zero-friction plan).
 * Home is Ask-only; this page hosts the enrichment wizard for a known
 * entity (or content-first when no entity is chosen yet).
 */
const V2AddKnowledgePage = ({ onNavigate }) => {
  const { tokens } = useV2Theme();
  const { isMobile } = useDeviceDetection();
  const { setChrome } = useMobileChrome();
  const guide = useOptionalProductGuide();
  const goAsk = () => {
    if (typeof guide?.guardNavigate === "function") {
      guide.guardNavigate(V2_ROUTES.home);
      return;
    }
    onNavigate?.(V2_ROUTES.home);
  };

  useEffect(() => {
    emitGuideEvent("home-panel", { panel: "add" });
  }, []);

  useEffect(() => {
    if (!isMobile) return undefined;
    setChrome({
      chromeMode: "tabs",
      contextTitle: "Add knowledge",
      onBack: onNavigate ? goAsk : null,
      backLabel: "Ask",
      headerTrailing: null,
      actionTray: null,
      hideShellHeader: false,
    });
    return undefined;
  }, [isMobile, onNavigate, setChrome, guide?.guardNavigate, guide?.focusActive]);

  return (
    <section
      className={v2cx(
        tokens.app.mainFlush,
        "flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-2"
      )}
    >
      <div className={`${tokens.surface.card} flex min-h-0 flex-1 flex-col overflow-hidden`}>
        {/* Single-line chrome: back + title + subtitle share one bar instead
            of a standalone back row stacked on a two-line card header. */}
        <div
          className={v2cx(
            tokens.surface.panelHeader,
            "flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 !py-2"
          )}
        >
          {!isMobile ? (
            <button
              type="button"
              onClick={goAsk}
              className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Ask
            </button>
          ) : null}
          <h2 className="shrink-0 text-sm font-semibold text-slate-900 sm:text-base">
            Add knowledge
          </h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-200 v2-explorer-light px-4 py-3 sm:px-5">
          <SystemInput
            onNavigateToGraph={
              onNavigate ? () => onNavigate(V2_ROUTES.explorer) : undefined
            }
            onNavigateToInbox={
              onNavigate ? () => onNavigate(V2_ROUTES.insightsInbox) : undefined
            }
          />
        </div>
      </div>
    </section>
  );
};

export default V2AddKnowledgePage;
